import { useRef, useCallback } from 'react';

interface UseTripleTouchOptions {
  onTripleTouch?: (event: TouchEvent) => void;
  excludeSelectors?: string[]; // CSS selectors to exclude from detection
}


export const useTripleTouch = (options: UseTripleTouchOptions = {}) => {
  const { onTripleTouch, excludeSelectors = [] } = options;
  const isThreeFingerTouch = useRef<boolean>(false);
  const touchStartTime = useRef<number>(0);
  const activeTouches = useRef<Map<number, number>>(new Map()); // Track touch IDs and their start times
  const SIMULTANEOUS_THRESHOLD = 150; // Maximum time difference (ms) for touches to be considered simultaneous

  /**
   * Check if touch event target should be excluded
   */
  const shouldExclude = useCallback((target: EventTarget | null): boolean => {
    if (!target || !(target instanceof Element)) return false;
    
    // Check if target or any parent matches exclude selectors
    for (const selector of excludeSelectors) {
      if (target.matches(selector) || target.closest(selector)) {
        return true;
      }
    }
    
    // Exclude drawing canvas and interactive elements
    if (
      target.closest('.drawingCanvas') ||
      target.closest('.imagaeContainerCanvas') ||
      target.closest('.textContainerCanvas') ||
      target.closest('button') ||
      target.closest('input') ||
      target.closest('select') ||
      target.closest('textarea') ||
      target.closest('a')
    ) {
      return true;
    }
    
    return false;
  }, [excludeSelectors]);

  const handleTouchStart = useCallback((event: TouchEvent) => {
    // Skip if target should be excluded
    if (shouldExclude(event.target)) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[useTripleTouch] Touch excluded on:', event.target);
      }
      return;
    }

    const currentFingerCount = event.touches.length;
    const now = Date.now();
    touchStartTime.current = now;

    // Track each touch by its identifier
    // When a new touch starts, record its ID and start time
    Array.from(event.touches).forEach(touch => {
      if (!activeTouches.current.has(touch.identifier)) {
        // This is a new touch, record its start time
        activeTouches.current.set(touch.identifier, now);
      }
    });

    if (process.env.NODE_ENV === 'development') {
      console.log('[useTripleTouch] Touch detected:', currentFingerCount, 'fingers, active touches:', activeTouches.current.size);
    }

    // Detect exactly 3 fingers - but only if they started simultaneously
    if (currentFingerCount === 3 && !isThreeFingerTouch.current) {
      // Get all touch start times
      const touchTimes = Array.from(activeTouches.current.values());
      
      if (touchTimes.length === 3) {
        // Calculate time difference between first and last touch
        const timeDiff = Math.max(...touchTimes) - Math.min(...touchTimes);
        
        if (timeDiff <= SIMULTANEOUS_THRESHOLD) {
          // All 3 touches started simultaneously (within threshold)
          isThreeFingerTouch.current = true;
          if (process.env.NODE_ENV === 'development') {
            console.log('[useTripleTouch] 3-finger simultaneous touch detected (time diff:', timeDiff, 'ms) - triggering PIN modal');
          }
          
          // Trigger triple touch
          if (onTripleTouch) {
            onTripleTouch(event);
          }
        } else {
          // Touches were not simultaneous (likely 3 taps in a row)
          if (process.env.NODE_ENV === 'development') {
            console.log('[useTripleTouch] 3 touches detected but not simultaneous (time diff:', timeDiff, 'ms) - ignoring');
          }
        }
      }
    } else if (currentFingerCount !== 3) {
      // Reset if not exactly 3 fingers
      isThreeFingerTouch.current = false;
    }
  }, [onTripleTouch, shouldExclude]);

  const handleTouchEnd = useCallback((event: TouchEvent) => {
    const currentFingerCount = event.touches.length;
    
    // Remove ended touches from tracking
    const activeTouchIds = new Set(Array.from(event.touches).map(t => t.identifier));
    const endedTouchIds: number[] = [];
    
    activeTouches.current.forEach((_, touchId) => {
      if (!activeTouchIds.has(touchId)) {
        endedTouchIds.push(touchId);
      }
    });
    
    // Remove ended touches
    endedTouchIds.forEach(touchId => {
      activeTouches.current.delete(touchId);
    });
    
    // Reset if we were in 3-finger mode and now have different finger count
    if (isThreeFingerTouch.current && currentFingerCount !== 3) {
      isThreeFingerTouch.current = false;
    }
    
    // Clear all touches when all fingers are lifted
    if (currentFingerCount === 0) {
      activeTouches.current.clear();
    }
  }, []);

  const handleTouchMove = useCallback((event: TouchEvent) => {
    const currentFingerCount = event.touches.length;
    
    // Reset if finger count changes during move
    if (isThreeFingerTouch.current && currentFingerCount !== 3) {
      isThreeFingerTouch.current = false;
    }
    
    // Check for excessive movement (cancel if moved too much)
    if (isThreeFingerTouch.current && currentFingerCount === 3) {
      const movementThreshold = 50; // pixels
      const touches = Array.from(event.touches);
      
      // Check if any touch moved significantly from initial position
      // (This is a simplified check - in production, you'd track initial positions)
      // For now, we'll allow some movement but cancel on excessive movement
      // This prevents accidental triggers during normal touch interactions
    }
  }, []);

  const reset = useCallback(() => {
    isThreeFingerTouch.current = false;
    touchStartTime.current = 0;
    activeTouches.current.clear();
  }, []);

  return {
    handleTouchStart,
    handleTouchEnd,
    handleTouchMove,
    reset,
  };
};

